import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Gauge, Activity, MapPin, Wrench, Zap, Menu, X, Lock, Newspaper, Wrench as ToolsIcon } from "lucide-react";
import { trackPageview, trackClick } from "../lib/analytics";
import { CrossLinks } from "./CrossLinks";

const links = [
  { to: "/speed-test", label: "Speed Test", icon: Gauge, testid: "nav-speed-test" },
  { to: "/down-detector", label: "Down Detector", icon: Activity, testid: "nav-down-detector" },
  { to: "/news", label: "News", icon: Newspaper, testid: "nav-news" },
  { to: "/tools", label: "Tools", icon: ToolsIcon, testid: "nav-tools" },
  { to: "/my-ip", label: "My IP", icon: MapPin, testid: "nav-my-ip" },
  { to: "/fix-my-internet", label: "Fix My Internet", icon: Wrench, testid: "nav-fix-my-internet" },
];

export function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);
  const location = useLocation();

  // Pageview tracking
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  // Close dropdown on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Click-away
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="min-h-screen flex flex-col bg-grid overflow-x-hidden">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[rgba(5,5,10,0.75)] backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" onClick={() => trackClick("brand-link")} className="flex items-center gap-2 group min-w-0" data-testid="brand-link">
            <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#4A78FF,#2D5AFF)", boxShadow: "0 0 24px -4px rgba(61,107,255,0.6)" }}>
              <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display font-semibold text-base sm:text-lg tracking-tight truncate">
              Speed<span className="text-[var(--cobalt-bright)]">Test</span>Tools
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                onClick={() => trackClick(`nav-${l.to}`)}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                    isActive
                      ? "text-white bg-[rgba(61,107,255,0.15)] border border-[rgba(61,107,255,0.4)]"
                      : "text-[var(--text-dim)] hover:text-white hover:bg-white/5 border border-transparent"
                  }`
                }
              >
                <l.icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop CTA */}
          <Link
            to="/speed-test"
            onClick={() => trackClick("header-cta-speed-test")}
            className="btn-cobalt hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
            data-testid="header-cta-speed-test"
          >
            <Gauge className="w-4 h-4" /> Run test
          </Link>

          {/* Mobile hamburger */}
          <div className="md:hidden relative" ref={dropRef}>
            <button
              onClick={() => setOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-md border border-white/10 bg-white/5 active:scale-95 transition"
              aria-label="Menu"
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {open && (
              <div
                className="absolute right-0 top-full mt-2 w-64 rounded-xl shadow-2xl overflow-hidden fade-up"
                style={{ background: "#0B0C14", border: "1px solid rgba(138,160,255,0.25)", backdropFilter: "blur(18px)" }}
                role="menu"
                data-testid="mobile-menu-dropdown"
              >
                <nav className="p-2">
                  {links.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`${l.testid}-mobile`}
                      onClick={() => trackClick(`nav-mobile-${l.to}`)}
                      className={({ isActive }) =>
                        `px-3 py-3 rounded-lg text-sm font-medium flex items-center gap-3 transition ${
                          isActive
                            ? "bg-[rgba(61,107,255,0.18)] text-white"
                            : "text-[var(--text-dim)] hover:bg-white/5 hover:text-white"
                        }`
                      }
                    >
                      <l.icon className="w-4 h-4" />
                      {l.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="border-t border-white/5 p-2">
                  <Link
                    to="/speed-test"
                    onClick={() => trackClick("mobile-cta-speed-test")}
                    className="btn-cobalt flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium"
                    data-testid="mobile-cta-speed-test"
                  >
                    <Gauge className="w-4 h-4" /> Run speed test
                  </Link>
                  <Link
                    to="/admin"
                    onClick={() => trackClick("mobile-nav-admin")}
                    className="mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-xs font-mono uppercase tracking-wider text-[var(--text-dim)] hover:text-white hover:bg-white/5 transition"
                    data-testid="mobile-nav-admin"
                  >
                    <Lock className="w-3.5 h-3.5" /> Admin
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 min-w-0">
        {children}
        <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 pb-12">
          <CrossLinks />
        </div>
      </main>

      <footer className="border-t border-white/5 py-8 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 text-sm text-[var(--text-muted)] flex flex-col gap-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-center md:text-left">© {new Date().getFullYear()} SpeedTestTools · Built for humans who just want the internet to work.</div>
            <div className="flex items-center gap-4 font-mono text-xs">
              <span>No sign-up · No ads · 100% free</span>
              <Link to="/admin" className="text-[var(--text-muted)] hover:text-[var(--cobalt-bright)] transition" data-testid="footer-admin-link">Admin</Link>
            </div>
          </div>

          {/* Powered by stxoshiAI */}
          <div className="flex items-center justify-center gap-3 pt-2 border-t border-white/5" data-testid="footer-powered-by">
            <span className="text-xs font-mono text-[var(--text-muted)]">Powered by</span>
            <a
              href="https://stxoshi.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick("footer-stxoshi-link")}
              className="inline-flex items-center gap-2 group"
              data-testid="footer-stxoshi-link"
            >
              <img
                src="/brand/stxoshi.png"
                alt="stxoshiAI"
                className="w-6 h-6 rounded-full ring-1 ring-white/10 group-hover:ring-[#F05A1A] transition"
                loading="lazy"
              />
              <span className="text-sm font-semibold text-white group-hover:text-[#F05A1A] transition">
                stxoshi<span className="text-[#F05A1A]">AI</span>
              </span>
            </a>
            <span className="text-[var(--text-muted)]">·</span>
            <a
              href="https://www.threads.com/stxoshi.btc"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick("footer-threads-link")}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/10 text-[var(--text-dim)] hover:text-white hover:border-white/30 transition"
              data-testid="footer-threads-link"
              aria-label="Threads"
            >
              <svg viewBox="0 0 256 256" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M186.4 119.1c-.85-.41-1.71-.8-2.59-1.17-1.53-28.23-16.95-44.38-42.85-44.55h-.35c-15.5 0-28.39 6.62-36.33 18.66l14.25 9.78c5.93-9 15.24-10.92 22.09-10.92h.24c8.53.05 14.96 2.53 19.13 7.36 3.03 3.53 5.06 8.41 6.06 14.58-7.53-1.28-15.67-1.67-24.38-1.18-24.53 1.42-40.3 15.73-39.24 35.62.54 10.09 5.57 18.77 14.16 24.45 7.27 4.8 16.63 7.15 26.37 6.62 12.86-.7 22.95-5.6 29.99-14.56 5.34-6.8 8.72-15.61 10.2-26.71 6.08 3.67 10.58 8.5 13.07 14.31 4.23 9.87 4.48 26.1-8.76 39.32-11.6 11.58-25.55 16.59-46.63 16.74-23.39-.17-41.08-7.67-52.56-22.28C77.76 172.21 72 151.8 71.79 128c.21-23.8 5.97-44.21 17.12-60.71C100.39 52.68 118.08 45.17 141.47 45c23.56.17 41.56 7.71 53.51 22.39 5.86 7.2 10.28 16.25 13.21 26.82l17.13-4.57c-3.55-13-9.04-24.2-16.44-33.29-15.31-18.81-37.71-28.45-66.35-28.65h-.12c-28.58.2-50.73 9.88-65.8 28.77C64.15 74.34 57.2 97.17 57 128v.14c.2 30.83 7.15 53.66 20.61 70.54 15.07 18.89 37.22 28.57 65.8 28.77h.12c25.4-.18 43.31-6.82 58.08-21.54 19.32-19.28 18.74-43.45 12.37-58.31-4.57-10.66-13.28-19.32-25.18-25.1zm-44.29 39.52c-10.78.61-21.98-4.23-22.53-14.54-.41-7.65 5.45-16.19 23.21-17.21 2.03-.12 4.03-.17 5.99-.17 6.45 0 12.48.63 17.97 1.83-2.04 25.51-14.02 29.51-24.64 30.09z"/>
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
